import React, { useState, useEffect, useCallback } from 'react';
import db from '../../lib/database';
import { useConfirm } from '../../hooks/useConfirm';
import { useToast } from '../../hooks/useToast';
import { IconPlus } from '../../components/icons';
import { NumberField, ChoiceKeys, Switch, Button, RowSkeleton, tableWrapCls, tableCls, thCls, thNumCls, trCls, trOpenCls, tdCls } from '../../components/ui';

// MaterialsManager — admin CRUD for the materials catalog.
//
// Stage 3 of the recap-flow buildout (sql/044_materials.sql). Each row
// holds a name, unit, cost (BBM's supplier cost — admin-only by
// convention; we show it here because Settings IS the admin surface),
// price (customer-facing), sort_order, and an active flag.
//
// Soft-delete only — historical recap snapshots in
// move_jobs.materials_used reference rows by id, and we need the names
// to keep resolving after a material is retired. The "Archive" button
// flips active=false; the row drops out of the crew portal picker and
// new recap captures, but old recaps still render fine.
//
// Reads via db.listMaterialTypes({ includeInactive: true }) so the
// admin sees archived rows alongside active ones (filterable below).

const UNIT_OPTIONS = [
    { value: 'each',  label: 'each' },
    { value: 'roll',  label: 'roll' },
    { value: 'pad',   label: 'pad' },
    { value: 'pack',  label: 'pack' },
    { value: 'sheet', label: 'sheet' },
];

const inputCls = 'w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 placeholder:text-slate-400';

const NEW_ROW_DEFAULTS = { name: '', unit: 'each', cost: 0, price: 0, sort_order: 100, active: true };

export default function MaterialsManager() {
    // In-app confirm (QA M2 NEW-9 native-dialog sweep).
    const { confirm: confirmDialog, confirmElement } = useConfirm();
    const { show: showToast } = useToast();
    const [rows, setRows] = useState(null);    // null while loading
    const [error, setError] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const [dirtyIds, setDirtyIds] = useState(() => new Set());
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState(null);  // editing-new-row state

    const refresh = useCallback(async () => {
        try {
            const list = await db.listMaterialTypes({ includeInactive: true });
            setRows(list);
            setDirtyIds(new Set());
        } catch (e) {
            setError(e?.message || 'Failed to load materials');
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const set = (id, key, value) => {
        setRows(prev => (prev || []).map(r => r.id === id ? { ...r, [key]: value } : r));
        setDirtyIds(prev => {
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    };

    const commit = async () => {
        if (dirtyIds.size === 0 && !draft) return;
        setSaving(true);
        try {
            // Persist edits to existing rows first
            for (const id of dirtyIds) {
                const row = (rows || []).find(r => r.id === id);
                if (!row) continue;
                await db.saveMaterialType(row);
            }
            // Then insert the new-row draft if it has a name
            if (draft && draft.name?.trim()) {
                await db.saveMaterialType(draft);
                setDraft(null);
            }
            await refresh();
            showToast('Materials saved');
        } catch (e) {
            showToast('Save failed: ' + (e?.message || e), 'error');
        } finally {
            setSaving(false);
        }
    };

    const archive = async (row) => {
        if (!(await confirmDialog({ title: `Archive ${row.name}?`, body: `${row.name} stops appearing on new recaps. Past recap entries that used it are kept.`, confirmLabel: `Archive ${row.name}`, danger: true }))) return;
        try {
            await db.deleteMaterialType(row.id);
            await refresh();
            showToast(`${row.name} archived`);
        } catch (e) {
            showToast('Archive failed: ' + (e?.message || e), 'error');
        }
    };

    const restore = async (row) => {
        try {
            await db.saveMaterialType({ ...row, active: true });
            await refresh();
            showToast(`${row.name} restored`);
        } catch (e) {
            showToast('Restore failed: ' + (e?.message || e), 'error');
        }
    };

    const visible = (rows || []).filter(r => showArchived || r.active);

    return (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h2 className="text-lg font-bold text-slate-900">Materials</h2>
                    <p className="text-[11px] text-slate-500">
                        Catalog of materials BBM uses on jobs. Cost is admin-only — the crew portal shows quantity pickers only.
                    </p>
                </div>
                <Switch
                    size="sm"
                    checked={!!showArchived}
                    onChange={v => setShowArchived(v)}
                    label="Show archived"
                    labelClassName="text-[11px] font-normal text-slate-600"
                />
            </div>

            {rows === null && <div className={tableWrapCls}><RowSkeleton rows={5} /></div>}
            {error && (
                <div className="text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mb-3">{error}</div>
            )}

            {rows !== null && (
                <div className={tableWrapCls}>
                    {/* Ledger recipe, see BBM Context for claude/lists.md §3 */}
                    <table className={tableCls}>
                        <thead>
                            <tr>
                                <th className={thCls}>Name</th>
                                <th className={thCls}>Unit</th>
                                <th className={`${thNumCls} w-24`} title="BBM cost — admin-only">Cost</th>
                                <th className={`${thNumCls} w-24`}>Price</th>
                                <th className={`${thNumCls} w-20`}>Sort</th>
                                <th className={`${thNumCls} w-20`}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map(r => (
                                <tr key={r.id} className={`${trCls} ${!r.active ? 'text-slate-400 opacity-70' : ''}`}>
                                    <td className={tdCls}>
                                        <input
                                            type="text"
                                            value={r.name}
                                            onChange={e => set(r.id, 'name', e.target.value)}
                                            disabled={!r.active}
                                            className={inputCls}
                                        />
                                    </td>
                                    <td className={tdCls}>
                                        <ChoiceKeys
                                            size="sm"
                                            wrap={false}
                                            items={UNIT_OPTIONS.find(u => u.value === r.unit) ? UNIT_OPTIONS : [...UNIT_OPTIONS, { value: r.unit, label: r.unit }]}
                                            value={r.unit}
                                            onChange={v => set(r.id, 'unit', v)}
                                            disabled={!r.active}
                                            ariaLabel={`Unit for ${r.name || 'material'}`}
                                        />
                                    </td>
                                    <td className={tdCls}>
                                        <NumberField
                                            size="sm"
                                            step={0.25}
                                            min={0}
                                            prefix="$"
                                            value={r.cost ?? 0}
                                            onChange={v => set(r.id, 'cost', v)}
                                            disabled={!r.active}
                                            ariaLabel={`Cost for ${r.name || 'material'}`}
                                            className="min-w-[132px]"
                                        />
                                    </td>
                                    <td className={tdCls}>
                                        <NumberField
                                            size="sm"
                                            step={0.25}
                                            min={0}
                                            prefix="$"
                                            value={r.price ?? 0}
                                            onChange={v => set(r.id, 'price', v)}
                                            disabled={!r.active}
                                            ariaLabel={`Price for ${r.name || 'material'}`}
                                            className="min-w-[132px]"
                                        />
                                    </td>
                                    <td className={tdCls}>
                                        <NumberField
                                            size="sm"
                                            step={1}
                                            min={0}
                                            value={r.sort_order ?? 100}
                                            onChange={v => set(r.id, 'sort_order', v)}
                                            disabled={!r.active}
                                            ariaLabel={`Sort order for ${r.name || 'material'}`}
                                            className="min-w-[116px]"
                                        />
                                    </td>
                                    <td className={`${tdCls} text-right`}>
                                        {r.active ? (
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => archive(r)}
                                                title="Stop showing on new recaps. History stays."
                                            >
                                                Archive
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="tertiary"
                                                size="sm"
                                                onClick={() => restore(r)}
                                                title="Re-activate this material"
                                            >
                                                Restore
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}

                            {/* New-row draft. Lives outside `rows` until
                                committed so the user can type without
                                churning the list. */}
                            {draft ? (
                                <tr className={`${trCls} ${trOpenCls}`}>
                                    <td className={tdCls}>
                                        <input
                                            type="text"
                                            value={draft.name}
                                            onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                                            placeholder="e.g. TV Box"
                                            className={inputCls}
                                            autoFocus
                                        />
                                    </td>
                                    <td className={tdCls}>
                                        <ChoiceKeys
                                            size="sm"
                                            wrap={false}
                                            items={UNIT_OPTIONS}
                                            value={draft.unit}
                                            onChange={v => setDraft(d => ({ ...d, unit: v }))}
                                            ariaLabel="Unit for new material"
                                        />
                                    </td>
                                    <td className={tdCls}>
                                        <NumberField
                                            size="sm"
                                            step={0.25}
                                            min={0}
                                            prefix="$"
                                            value={draft.cost}
                                            onChange={v => setDraft(d => ({ ...d, cost: v }))}
                                            ariaLabel="Cost for new material"
                                            className="min-w-[132px]"
                                        />
                                    </td>
                                    <td className={tdCls}>
                                        <NumberField
                                            size="sm"
                                            step={0.25}
                                            min={0}
                                            prefix="$"
                                            value={draft.price}
                                            onChange={v => setDraft(d => ({ ...d, price: v }))}
                                            ariaLabel="Price for new material"
                                            className="min-w-[132px]"
                                        />
                                    </td>
                                    <td className={tdCls}>
                                        <NumberField
                                            size="sm"
                                            step={1}
                                            min={0}
                                            value={draft.sort_order}
                                            onChange={v => setDraft(d => ({ ...d, sort_order: v }))}
                                            ariaLabel="Sort order for new material"
                                            className="min-w-[116px]"
                                        />
                                    </td>
                                    <td className={`${tdCls} text-right`}>
                                        <Button
                                            variant="tertiary"
                                            size="sm"
                                            onClick={() => setDraft(null)}
                                        >
                                            Cancel
                                        </Button>
                                    </td>
                                </tr>
                            ) : null}

                            {visible.length === 0 && !draft && (
                                <tr>
                                    <td colSpan={6} className="px-3 py-6 text-center text-sm text-slate-400">
                                        {showArchived ? 'No materials.' : 'No active materials. Add some below.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
                <Button
                    variant="tertiary"
                    onClick={() => setDraft({ ...NEW_ROW_DEFAULTS })}
                    disabled={!!draft}
                    leftIcon={<IconPlus size={14}/>}
                >
                    Add material
                </Button>
                <Button
                    variant="primary"
                    onClick={commit}
                    loading={saving}
                    disabled={dirtyIds.size === 0 && !draft?.name?.trim()}
                >
                    {dirtyIds.size > 0 && draft?.name?.trim()
                        ? `Save ${dirtyIds.size} edit${dirtyIds.size === 1 ? '' : 's'} + new`
                        : dirtyIds.size > 0
                            ? `Save ${dirtyIds.size} edit${dirtyIds.size === 1 ? '' : 's'}`
                            : draft?.name?.trim()
                                ? 'Save new material'
                                : 'Save'}
                </Button>
            </div>
            {confirmElement}
        </div>
    );
}
